/**
 * Images des pages locales — URLs Unsplash testées HTTP 200 + assets locaux en fallback.
 *
 * IMPORTANT : Toutes les URLs Unsplash ci-dessous ont été vérifiées via curl
 * (statut HTTP 200) le 2026-05-18. Si l'une casse à l'avenir, le composant
 * SmartImage affichera un fallback gradient.
 */

import mariageHero from '@/assets/mariage-prestige-hero.webp';
import anniversaireHero from '@/assets/anniversaire-signature-hero.webp';
import partnerHero from '@/assets/partner-hero-teamwork.jpg';
import partnerTable from '@/assets/partner-team-table.jpg';

const UNSPLASH = (id: string, w = 1920) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

// IDs Unsplash testés HTTP 200 le 2026-05-18 (curl validé)
const IDS = {
  // Wedding général
  weddingTable: '1519741497674-611481863552',       // table mariage prestige
  weddingBride: '1511795409834-ef04bbd61622',       // mariée
  weddingCeremony: '1519225421980-715cb0215aed',    // cérémonie
  weddingOutdoor: '1606216794074-735e91aa2c92',     // cérémonie outdoor
  weddingIntimate: '1510076857177-7470076d4098',    // cérémonie intime
  weddingOutdoor2: '1591604466107-ec97de577aff',    // wedding outdoor
  // Château / palais
  chateau: '1564501049412-61c2a3083791',            // château français
  chateauEurope: '1503095396549-807759245b35',      // château europe
  // Mariages culturels
  mariageAfro: '1606800052052-a08af7148866',        // mariage afro
  mariageAsiatique: '1532274402911-5a369e4c4bb5',   // mariage asiatique
  mariageJuif: '1583939003579-730e3918a45a',        // mariage cérémonie
  mariageOriental: '1604017011826-d3b4c23f8914',    // mariage oriental/déco
  mariageIndien: '1497644083578-611b798c60f3',      // mariage indien/coloré
  // Anniversaire / cocktail
  anniversaire: '1530103862676-de8c9debad1d',       // anniversaire bal
  cocktail: '1492684223066-81342ee5ff30',           // cocktail
  partyVibes: '1530023367847-a683933f4172',         // soirée
  // Demande mariage / romance
  proposal: '1518621736915-f3b1c41bfd00',           // demande romance
  // Business / corporate
  business: '1540575467063-178a50c2df87',           // conférence business
  // Paris / lieux
  pontParis: '1499856871958-5b9627545d1a',          // Pont Alexandre III
} as const;

export const LOCAL_PAGES_IMAGES = {
  // Wedding Planner Paris — table mariage parisienne
  weddingParis: {
    hero: UNSPLASH(IDS.weddingTable),
    body: mariageHero,
    card: UNSPLASH(IDS.weddingTable, 800),
  },
  // Wedding Planner Seine-et-Marne — château français
  weddingSeineEtMarne: {
    hero: UNSPLASH(IDS.chateau),
    body: UNSPLASH(IDS.chateauEurope),
    card: UNSPLASH(IDS.chateau, 800),
  },
  // Séminaire La Défense — conférence business
  seminaireLaDefense: {
    hero: UNSPLASH(IDS.business),
    body: partnerHero,
    card: UNSPLASH(IDS.business, 800),
  },
  // Versailles & Yvelines — Pont Alexandre III (Paris romance, élégance)
  versailles: {
    hero: UNSPLASH(IDS.pontParis),
    body: UNSPLASH(IDS.chateauEurope),
    card: UNSPLASH(IDS.pontParis, 800),
  },
  // Privatisation de lieu à Paris — soirée en lieu privatisé
  privatisation: {
    hero: UNSPLASH(IDS.partyVibes),
    body: UNSPLASH(IDS.cocktail),
    card: UNSPLASH(IDS.partyVibes, 800),
  },
  // Anniversaire Prestige Paris — bal anniversaire
  anniversaire: {
    hero: UNSPLASH(IDS.anniversaire),
    body: UNSPLASH(IDS.cocktail),
    card: UNSPLASH(IDS.anniversaire, 800),
  },
  // Calculateur — image business neutre (asset local)
  calculateur: {
    card: partnerTable,
  },
  // Mariage générique (fallback ultra-safe)
  mariageGeneric: {
    card: mariageHero,
  },
  // Pages mariage culturels (cards différenciées)
  mariageJuifCard: UNSPLASH(IDS.mariageJuif, 800),
  mariageOrientalCard: UNSPLASH(IDS.mariageOriental, 800),
  mariageAfroCard: UNSPLASH(IDS.mariageAfro, 800),
  mariageChinoisCard: UNSPLASH(IDS.mariageAsiatique, 800),
  mariageIndienCard: UNSPLASH(IDS.mariageIndien, 800),
  mariageLaiqueCard: UNSPLASH(IDS.weddingOutdoor, 800),
  mariageIntimeCard: UNSPLASH(IDS.weddingIntimate, 800),
  propositionCard: UNSPLASH(IDS.proposal, 800),
} as const;

// Export individuel des IDs pour usage direct dans les pages (hero différencié)
export const UNSPLASH_IDS = IDS;
export const UNSPLASH_URL = UNSPLASH;
